import {
  useUpdateExamEventMutation,
  GetAllExamEventsByExamCenterCodeDocument,
  RegistrationStatus,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useParams, useNavigate } from '@exam-center/router';
import {
  CancelOutlinedIcon,
  EditOutlinedIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
  VisibilityOutlinedIcon,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';

import { type ExamEventCardProps } from '../../components';
import { useExamEventScheduleModal } from '../../providers';

interface OptionActions {
  onViewExamEvent: (examEventId: string) => void;
  onEditExamEvent: (examEventId: string) => void;
  onCancelRegistration: (examEventId: string) => void;
}

const viewOptionLabel: MoreMenuIconButtonOption = {
  icon: <VisibilityOutlinedIcon />,
  label: 'ดูรายละเอียด',
};

const editOptionLabel: MoreMenuIconButtonOption = {
  icon: <EditOutlinedIcon />,
  label: 'แก้ไขรอบสอบ',
};

const cancelOptionLabel: MoreMenuIconButtonOption = {
  icon: (
    <CancelOutlinedIcon
      sx={(theme) => ({
        color: theme.palette.error.main,
      })}
    />
  ),
  label: 'ยกเลิกรอบสอบ',
  sx: {
    color: (theme) => theme.palette.error.main,
    ':hover': {
      backgroundColor: (theme) => theme.palette.primary.lighter,
    },
  },
};

function getExamEventMenuOptions(
  examEventId: string,
  status: ExamEventCardProps['variant'],
  actions: OptionActions,
  canUpdate: boolean,
): MoreMenuIconButtonOption[] {
  if (status === 'canceled') {
    return [];
  }
  const { onViewExamEvent, onEditExamEvent, onCancelRegistration } = actions;

  const menuOptions: MoreMenuIconButtonOption[] = [];
  if (['open-for-registration', 'temporarily-closed', 'session-full'].includes(status)) {
    if (canUpdate) {
      menuOptions.push({
        ...editOptionLabel,
        onClick: () => onEditExamEvent(examEventId),
      });
      menuOptions.push({
        ...cancelOptionLabel,
        onClick: () => onCancelRegistration(examEventId),
      });
    } else {
      menuOptions.push({
        ...viewOptionLabel,
        onClick: () => onViewExamEvent(examEventId),
      });
    }
  }

  if (status === 'registration-closed') {
    menuOptions.push({
      ...viewOptionLabel,
      onClick: () => onViewExamEvent(examEventId),
    });
  }

  return menuOptions;
}

export interface ExamEventMenusProps {
  examEvent: { id: string; variant: ExamEventCardProps['variant'] };
}

export default function ExamEventMenus(props: Readonly<ExamEventMenusProps>) {
  const { examEvent } = props;

  const { examCenterCode } = useParams('/:examCenterCode/exam-events/years/:year/months/:month');

  const { showPromiseSnackbar } = useSnackbar();
  const { setAction } = useExamEventScheduleModal();
  const navigate = useNavigate();

  const canUpdate = useHasPermission(APP_PERMISSIONS.EXAM_EVENT_MANAGEMENT.EXAM_EVENT.UPDATE);

  const [updateExamEvent] = useUpdateExamEventMutation({
    refetchQueries: [
      {
        query: GetAllExamEventsByExamCenterCodeDocument,
      },
    ],
  });

  const handleCancelExamEvent = (examEventId: string, reason: string) => {
    showPromiseSnackbar({
      message: {
        success: 'ยกเลิกรอบสอบเรียบร้อยแล้ว',
        error: 'ไม่สามารถยกเลิกรอบสอบได้',
        pending: 'กำลังยกเลิกรอบสอบ',
      },
      promise: async () =>
        await updateExamEvent({
          variables: {
            updateExamEventInput: {
              id: examEventId,
              toRegistrationStatus: {
                status: RegistrationStatus.Canceled,
                reason,
              },
            },
          },
        }),
    });
  };

  const handleClickCancelExamEvent = (examEventId: string) => {
    setAction({
      action: 'cancel',
      data: {
        onSubmit: (reason) => handleCancelExamEvent(examEventId, reason),
      },
    });
  };

  const actions = {
    onViewExamEvent: (examEventId: string) => {
      navigate('/:examCenterCode/exam-events/:examEventId', {
        params: {
          examCenterCode,
          examEventId,
        },
      });
    },
    onEditExamEvent: (examEventId: string) => {
      navigate('/:examCenterCode/exam-events/:examEventId', {
        params: {
          examCenterCode,
          examEventId,
        },
      });
    },
    onCancelRegistration: handleClickCancelExamEvent,
  };

  const menuOptions: MoreMenuIconButtonOption[] = getExamEventMenuOptions(
    examEvent.id,
    examEvent.variant,
    actions,
    canUpdate,
  );
  if (menuOptions.length === 0) return null;

  return <MoreMenuIconButton options={menuOptions} />;
}
